import React, { useState } from 'react';
import { MoreVertical, UserX, Crown, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';

/**
 * Host-only popover menu on a convoy member pill.
 * Props:
 *  - convoyId
 *  - memberId
 *  - memberName
 *  - onUpdated(updatedConvoyDict)
 */
export default function ConvoyMemberMenu({ convoyId, memberId, memberName, onUpdated }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'kick' | 'transfer' | null
  const [loading, setLoading] = useState(false);

  const run = async (path, successMsg) => {
    setLoading(true);
    try {
      const { data } = await api.post(path);
      toast.success(successMsg);
      onUpdated?.(data);
      setConfirm(null);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  const kick = () => run(`/convoys/${convoyId}/kick/${memberId}`, t('convoys.host.kicked'));
  const transfer = () => run(`/convoys/${convoyId}/transfer/${memberId}`, t('convoys.host.transferred'));

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`convoy-member-manage-${memberId}`}
            className="inline-grid place-items-center w-6 h-6 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition"
            aria-label={t('convoys.host.manage')}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="w-56 p-1 bg-[#0F0F12] border border-white/10 text-white text-sm"
        >
          <button
            type="button"
            data-testid={`convoy-member-transfer-${memberId}`}
            onClick={() => { setOpen(false); setConfirm('transfer'); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 transition text-left"
          >
            <Crown className="w-4 h-4 text-[#FFD479]" />
            <span>{t('convoys.host.transfer_to')} {memberName}</span>
          </button>
          <button
            type="button"
            data-testid={`convoy-member-kick-${memberId}`}
            onClick={() => { setOpen(false); setConfirm('kick'); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-red-500/10 text-red-300 hover:text-red-200 transition text-left"
          >
            <UserX className="w-4 h-4" />
            <span>{t('convoys.host.kick_member')}</span>
          </button>
        </PopoverContent>
      </Popover>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0F0F12] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">
              {confirm === 'kick' ? t('convoys.host.kick_confirm_title') : t('convoys.host.transfer_confirm_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirm === 'kick'
                ? `${t('convoys.host.kick_confirm_body')} ${memberName} ?`
                : `${t('convoys.host.transfer_confirm_body')} ${memberName} ?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="convoy-confirm-cancel"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="convoy-confirm-action"
              disabled={loading}
              onClick={(e) => { e.preventDefault(); confirm === 'kick' ? kick() : transfer(); }}
              className={confirm === 'kick'
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-[#FFD479] hover:bg-[#FFD479]/90 text-black'}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
